import * as members from '../privates/privateMembers';
import * as postMessage from '../privates/postMessage';
import platformUtils from '../privates/platformUtils';
import {validate} from '../privates/validations/validation';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import siteImpl from './impl/siteImpl';
import siteStructureUtils from '../privates/utils/siteStructureUtils';
import _ from 'lodash';

/**
 * @namespace wix-site
 * @summary The wix-site module contains functionality for obtaining information about your site and its pages.
 * @description
 *  To use the site module, import `wixSite` from the `wix-site` module:
 *
 *    ```javascript
 *    import wixSite from 'wix-site';
 *    ```
 *
 *  The APIs in `wix-site` can only be used in front-end code.
 */

/**
 * @typedef {external:Object} wix-site~SiteStructure
 * @summary An object that contains information about the site's pages, prefixes, and lightboxes.
 * @description
 *  The `SiteStructure` object is a flattened representation of the site. Use
 *  the [`getSiteStructure()`](#getSiteStructure) function to get the site's
 *  `SiteStructure` object. The structure does not include the headers and links
 *  from the site's **Pages** menu.
 * @snippet [Site-getSiteStructure.es6=Get information about the site's pages, prefixes, and lightboxes]
 *  This example gets the structure of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {wix-site~StructurePage[]} pages The pages of the site.
 * @property {wix-site~Prefix[]} prefixes The prefixes of the site's routers and dynamic pages.
 * @property {wix-site~StructureLightbox[]} lightboxes The site's lightboxes.
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~StructurePage
 * @summary An object that contains information about a page on the site.
 * @description
 *  The `StructurePage` object represents a page in the site. The page can be a
 *  regular page, dynamic page, router page, or page from an app.
 * @snippet [Site-currentPage_page.es6=Get information about the current page]
 * @snippet [Site-pages.es6=Get information about the site's pages]
 *  This example gets the pages of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The of the page.
 * @property {external:String} type The type of the page: `"static"` or `"template"`.
 * @property {external:String} [url] The URL of the page. If the page is a `static` page the `url` property is the page's SEO URL.
 * @property {external:String} [applicationId] If the page is an app page, the `applicationId` property is the app's ID.
 * @property {external:String} [prefix] If the page is a router or dynamic page, the `prefix` property is the page's prefix.
 * @property {external:Boolean} [isHomePage] If the page is the Home page, `isHomePage` is `true`. Otherwise, it does not exist.
 * @see [SiteStructure](#SiteStructure)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~Prefix
 * @summary An object that contains information about the site's prefixes.
 * @description
 *  A site's routers, dynamic pages, and app pages all have prefixes.
 * @snippet [Site-prefixes.es6=Get information about the site's prefixes]
 *  This example gets the prefixes of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The name of the prefix.
 * @property {external:String} type The type of the prefix: `"dynamicPages"`, `"router"`, or `"app"`.
 * @property {external:String} prefix The prefix.
 * @property {external:String} [applicationId] If the prefix is an app's prefix, the `applicationId` property is the app's ID.
 * @see [SiteStructure](#SiteStructure)
 * @see [currentPage](#currentPage)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

/**
 * @typedef {external:Object} wix-site~StructureLightbox
 * @summary An object that contains information about a lightbox on the site.
 * @snippet [Site-currentPage_lightbox.es6=Get information about the current lightbox]
 * @snippet [Site-lightboxes.es6=Get information about the site's lightboxes]
 *  This example gets the lightboxes of the site with the following in its pages menu:
 *  ![Site Structure](images/site_structure.png "Site Structure")
 * @property {external:String} name The name of the lightbox.
 * @see [SiteStructure](#SiteStructure)
 * @see [currentPage](#currentPage)
 * @see [getSiteStructure( )](#getSiteStructure)
 * @memberof wix-site
 */

class Site {

    constructor(appDefId){
        members._appDefId.set(this, appDefId);
        _.bindAll(this, ['routerSitemap']);
    }

    /**
     * @function routerSitemap
     * @syntax
     * function routerSitemap(routerPrefix: string): Promise<WixRouterSitemapEntry[]>
     * @summary Returns the sitemap for a router or dynamic page prefix.
     * @description
     *  The `routerSitemap()` function returns a Promise that resolves to a list of
     *  [`WixSitemapEntry`](wix-router.WixRouterSitemapEntry.html) objects. Each
     *  [`WixSitemapEntry`](wix-router.WixRouterSitemapEntry.html) includes
     *  information about a page, such as its URL, title, and name.
     *
     *  A sitemap is used by search engines to find the links to the site's pages.
     * @snippet [Site-routerSiteMap.es6=Get the sitemap for a prefix]
     * @param {external:String} routerPrefix The prefix of the router to get the sitemap for.
     * @returns {external:Promise}
     * @fulfill {wix-router.WixRouterSitemapEntry[]} A list of sitemap entries.
     * @reject {external:Error} The error that caused the rejection.
     * @memberof wix-site
     */
    routerSitemap(prefix) {
        return new Promise((resolve, reject) => {
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.GET_ROUTERS_SITEMAP,
                data: {prefix},
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function getSiteStructure
     * @syntax
     * function wixSite.getSiteStructure(): SiteStructure
     * @summary Returns information about the site's pages, prefixes, and lightboxes.
     * @snippet [Site-getSiteStructure.es6=Get information about the site's pages, prefixes, and lightboxes]
     *  This example gets the structure of the site with the following in its pages menu:
     *  ![Site Structure](images/site_structure.png "Site Structure")
     * @returns {wix-site~SiteStructure} An object that contains information about the site's pages, prefixes, and lightboxes.
     * @see [currentPage](#currentPage)
     * @memberof wix-site
     */
    getSiteStructure() {
        return siteImpl.getSiteStructure({includePageId: false});
    }

    /**
     * @member currentPage
     * @label property
     * @summary Gets information about the current page or lightbox.
     * @snippet [Site-currentPage_page.es6=Get information about the current page]
     * @snippet [Site-currentPage_lightbox.es6=Get information about the current lightbox]
     * @type {wix-site~StructurePage|wix-site~StructureLightbox}
     * @memberof wix-site
     * @see [getSiteStructure( )](#getSiteStructure)
     */
    get currentPage() {
        return siteImpl.getCurrentPage({includePageId: false});
    }

    getAppToken(appDefinitionId) {
        const isValid = validate({
            compType: 'Site',
            functionName: 'getAppToken',
            dataToValidate: {appDefinitionId}
        });
        if (!isValid) {
            return;
        }
        const appData = platformUtils.getRGI().getAppData(appDefinitionId);
        if (appData) {
            return appData.instance;
        }
        reporter.reportSdkError(generateError.error_app_does_not_exist({
            appDefId: appDefinitionId
        }));
    }

    /**
     * @member regionalSettings
     * @label property
     * @summary Gets the site's regional settings.
     * @description
     *  The retrieved regional settings corresponds to the regional settings
     *  that has been entered in the General Info section of your site's Dashboard.
     * 
     *  The regional settings are used to determine how numbers, dates, and currencies 
     *  are displayed on your site.
     * @snippet [Site-regionalSettings.es6=Get the site's regionalSettings]
     * @type {external:String}
     * @memberof wix-site
     * @readonly
     */
    get regionalSettings() {
        return platformUtils.getRGI().getRegionalSettings().locale;
    }

    /**
     * @member language
     * @label property
     * @summary Gets a code representing the site's language.
     * @description
     *  The retrieved language code corresponds to the main site language 
     *  as found in the General Info section of your site's Dashboard.
     * @note
     *  If you have a multilingual site, and it is being viewed in a language
     *  that is not the main site language, the `language` property still returns
     *  the main site language. To work with the languages of a multilingual site,
     *  use the [Multilingual API](wix-window.multilingual.html).
     * @snippet [Site-language.es6=Get the site's language]
     * @type {external:String}
     * @memberof wix-site
     * @readonly
     */
    get language() {
        return platformUtils.getRGI().getRegionalSettings().language;
    }

    /**
     * @member currency
     * @label property
     * @summary Gets a code representing the site's currency.
     * @description
     *  The retrieved currency code corresponds to the currency that has been
     *  entered in the General Info section of your site's Dashboard.
     * 
     *  The currency is used your site, apps (e.g. Wix Stores, Wix Bookings), and other 
     *  Wix features.
     * @snippet [Site-currency.es6=Get the site's currency]
     * @type {external:String}
     * @memberof wix-site
     * @readonly
     */
    get currency() {
        return platformUtils.getRGI().getRegionalSettings().currency;
    }

    /**
     * @member timezone
     * @label property
     * @summary Gets the site's timezone.
     * @description
     *  The retrieved timezone is the timezone that has been
     *  entered in the General Info section of your site's Dashboard.
     * 
     *  The timezone is used your site, apps (e.g. Wix Stores, Wix Bookings), and other 
     *  Wix features.
     * @snippet [Site-timezone.es6=Get the site's timezone]
     * @type {external:String}
     * @memberof wix-site
     * @readonly
     */
    get timezone() {
        return platformUtils.getRGI().getRegionalSettings().timezone;
    }

    /**
     * @member revision
     * @label property
     * @summary Gets the site revision ID.
     * @description
     *  The site revision ID is a unique identifier for the current revision of
     *  your site. Each time you manually save your site or publish your site
     *  following a change, a new revision is created.
     *
     * You can view and manage your site revisions in your <a href="https://support.wix.com/en/article/viewing-and-managing-your-site-history" target="_blank">Site History</a>.
     * You can use <a href="https://support.wix.com/en/article/about-site-monitoring#site-revisions" target="_blank">site monitoring</a> to
     * monitor and debug new revisions of your site.
     * @note
     *  Since publishing your site may change the revision ID, the ID when
     *  previewing your site may be different than the ID for the published site.
     *
     *  The site revision ID is guaranteed to be unique, but not meaningful.
     *  Therefore, you cannot predict future revision IDs before they are created.
     * @snippet [Site-revision_get.es6=Get the site revision ID]
     * @type {external:String}
     * @memberof wix-site
     */
    get revision() {
        return platformUtils.getRGI().getSiteRevision();
    }

    prefetchPageResources(itemsToLoad) {
        if (!validate({compType: 'Site', functionName: 'prefetchPageResources', dataToValidate: {itemsToLoad}}) ||
            !validate({compType: 'Site', functionName: 'prefetchPageResources', dataToValidate: {...itemsToLoad}})) {
            return;
        }

        const siteStructure = platformUtils.getRMI().getSiteStructure();
        const pageIds = [];
        const errPages = [];
        const errLightboxes = [];
        _.forEach(itemsToLoad.pages, (pageUrl) => {
            const pageId = siteStructureUtils.getPageIdByUrl(siteStructure, pageUrl);
            if (pageId) {
                pageIds.push(pageId);
            } else {
                errPages.push(pageUrl);
            }
        });
        _.forEach(itemsToLoad.lightboxes, (lightboxName) => {
            const pageId = siteStructureUtils.getPageIdByLightboxName(siteStructure, lightboxName);
            if (pageId) {
                pageIds.push(pageId);
            } else {
                errLightboxes.push(lightboxName);
            }
        });

        if (!_.isEmpty(pageIds)) {
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.PREFETCH_PAGE_RESOURCES,
                data: {pageIds},
                appDefinitionId: members._appDefId.get(this)
            });
        }
        if (_.isEmpty(errPages) && _.isEmpty(errLightboxes)) {
            return {code: 200, message: 'success'};
        }
        return {errors: {lightboxes: errLightboxes, pages: errPages, code: 404, message: 'Not Found'}};
    }

    /*
    getAppPublicAPI(appDefinitionId) {
        const RMI = platformUtils.getRMI();
        return RMI.getAppPublicAPI(appDefinitionId);
    }
    */
}

export default Site;
